import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Avatar from '@mui/material/Avatar';

import DeleteIcon from '@mui/icons-material/Delete';

import { getInitialsFromName } from '@ts-core/utils/utils';
import PageContentLayout from '@ts-core/core/components/PageContentLayout';

import ConfirmButton from '@ts-core/components/ConfirmButton';
import Dialog, { useDialog } from '@ts-core/components/Dialog';

import { useCourse } from 'modules/courses/hooks';
import { useCourseProfessors } from '../../hooks';
import { AcademyProfessor } from 'modules/academies/types';
import { CourseProfessor } from 'modules/courses/types';

import AddProfessorToCourse from './AddProfessorToCourse';

const ProfessorsPage = () => {
  const intl = useIntl();
  const { courseId } = useParams<{ courseId: string }>();

  const { professors = [], availableAcademyProfessors = [], onAddProfessorCourse, onRemoveProfessorCourse } = useCourseProfessors({ courseId });
  const { course } = useCourse({ courseId });
  const { onOpenDialog, onCloseDialog, dialogProps } = useDialog();

  const onRemoveProfessor = useCallback(
    (professor: CourseProfessor) => () => {
      onRemoveProfessorCourse(professor.id);
    },
    [onRemoveProfessorCourse]
  );

  const handleAddProfessor = useCallback(
    (professor: AcademyProfessor) => {
      onAddProfessorCourse(professor, () => onCloseDialog());
    },
    [onAddProfessorCourse, onCloseDialog]
  );

  const handleOpenAddProfessorDialog = useCallback(() => {
    onOpenDialog({
      title: intl.formatMessage({ id: 'courseProfessors.addProfessor' }),
    });
  }, [onOpenDialog, intl]);

  return (
    <Box>
      <PageContentLayout>
        <Box width="100%" display="flex" flexDirection="row" justifyContent="space-between">
          <Typography gutterBottom variant="h6">
            <FormattedMessage id="courseProfessors.title" />
          </Typography>
          <Button onClick={handleOpenAddProfessorDialog}>
            <FormattedMessage id="courseProfessors.addProfessor" />
          </Button>
        </Box>
        <Divider />
        {professors.length === 0 && (
          <Typography style={{ marginTop: 16 }} variant="subtitle1">
            No hay otros profesores en este curso
          </Typography>
        )}
        <List>
          {professors.map((professor) => (
            <Box key={professor.id}>
              <ListItem>
                <ListItemAvatar>
                  <Avatar alt={professor.name}>{getInitialsFromName(professor.name)}</Avatar>
                </ListItemAvatar>
                <ListItemText primary={professor.name} secondary={professor.email} />
                <ListItemSecondaryAction>
                  <ConfirmButton
                    alertMessage={intl.formatMessage(
                      { id: 'dialog.confirmRemoveStudentFromCourse.title' },
                      { student: professor.name, course: course?.name }
                    )}
                    onConfirm={onRemoveProfessor(professor)}
                  >
                    {({ onClick }) => (
                      <IconButton onClick={onClick} size="large">
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </ConfirmButton>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
            </Box>
          ))}
        </List>
      </PageContentLayout>
      <Dialog {...dialogProps}>
        <AddProfessorToCourse professors={availableAcademyProfessors} onAddProfessor={handleAddProfessor} />
      </Dialog>
    </Box>
  );
};

export default ProfessorsPage;
